import type { GenerationAuthStyle, GenerationResponseKind } from '@mpr/shared';

/**
 * Comparing a connector with the catalogue entry it started from, field by
 * field, so an update can be taken row by row instead of all or nothing.
 *
 * Only the HTTP contract is compared. Name, key, default flag, routing slot
 * and pricing are the operator's own and are never part of it.
 */

export const CONTRACT_FIELDS = [
  'requestTemplate',
  'responsePath',
  'responseKind',
  'responseMimePath',
  'pollPath',
  'pollUrlTemplate',
  'pollDonePath',
  'pollDoneValue',
  'pollFailedValues',
  'authOnResultFetch',
  'extraHeaders',
  'authStyle',
  'authParamName',
  'endpointUrl',
  'model',
] as const;
export type ContractField = (typeof CONTRACT_FIELDS)[number];

export interface ContractSnapshot {
  endpointUrl: string;
  authStyle: GenerationAuthStyle;
  authParamName: string | null;
  model: string | null;
  requestTemplate: unknown | null;
  responsePath: string | null;
  responseKind: GenerationResponseKind | null;
  responseMimePath: string | null;
  pollPath: string | null;
  pollUrlTemplate: string | null;
  pollDonePath: string | null;
  authOnResultFetch: boolean;
  extraHeaders: Record<string, string> | null;
  pollDoneValue: string | null;
  pollFailedValues: string | null;
}

export interface DiffRow {
  field: ContractField;
  before: unknown;
  after: unknown;
  /** Off for the fields people often customise on purpose. */
  checkedByDefault: boolean;
}

/** The model and the endpoint are the ones an operator typically changed to suit themselves. */
const UNCHECKED_BY_DEFAULT = new Set<ContractField>(['model', 'endpointUrl']);

/** Same JSON whatever the key order, so a reordered template is not a difference. */
function canonical(value: unknown): string {
  const sort = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(sort);
    if (typeof input === 'object' && input !== null) {
      return Object.fromEntries(
        Object.entries(input as Record<string, unknown>)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, inner]) => [key, sort(inner)]),
      );
    }
    return input;
  };
  return JSON.stringify(sort(value ?? null));
}

/** Blank, null and an empty header set all mean "not set". */
function normalised(value: unknown): unknown {
  if (value === undefined || value === '') return null;
  if (typeof value === 'string') return value.trim() === '' ? null : value.trim();
  if (typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length === 0) {
    return null;
  }
  return value;
}

export function sameContractValue(a: unknown, b: unknown): boolean {
  return canonical(normalised(a)) === canonical(normalised(b));
}

/**
 * The fields where the connector differs from the preset, in the order the
 * form shows them. A response kind the connector's capability cannot have
 * (a text kind on a media connector, or the reverse) is left out: taking it
 * would only produce a connector the server refuses.
 */
export function diffAgainstPreset(
  current: ContractSnapshot,
  preset: ContractSnapshot,
  capability: 'SCRIPT' | 'AUDIO' | 'VIDEO' | 'IMAGE',
): DiffRow[] {
  const rows: DiffRow[] = [];
  for (const field of CONTRACT_FIELDS) {
    const before = current[field];
    const after = preset[field];
    if (sameContractValue(before, after)) continue;
    if (field === 'responseKind' && after !== null) {
      const text = after === 'TEXT';
      if (text !== (capability === 'SCRIPT')) continue;
    }
    rows.push({ field, before, after, checkedByDefault: !UNCHECKED_BY_DEFAULT.has(field) });
  }
  return rows;
}

/** A value as the settings form types it: JSON text for the two objects, '' for not set. */
export function formText(value: unknown): string | boolean {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

/** The form fields (named as the contract fields are) that applying these rows sets. */
export function rowsToFormPatch(rows: DiffRow[]): Partial<Record<ContractField, string | boolean>> {
  const patch: Partial<Record<ContractField, string | boolean>> = {};
  for (const row of rows) {
    const text = formText(row.after);
    patch[row.field] = row.field === 'extraHeaders' && text === '{}' ? '' : text;
  }
  return patch;
}

/** A value for showing in a before/after cell. */
export function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}
